import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraInlineBanner } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '../../../../contexts/featureFlagContext';
import { useProjectVolumes } from '../../../../hooks/useProjectVolumes';
import { useVolumeDocuments } from '../../../../hooks/useVolumeDocuments';
import { useVolumeLegacyText } from '../hooks/useVolumeLegacyText';
import { ProposalVolumeWorkspace } from '../volume/ProposalVolumeWorkspace';
import { SectionMappingContext } from '../volume/SectionMappingContext';
import { SectionPadShell } from '../volume/SectionPadShell';
import { TemplateWorkspace } from '../volume/TemplateWorkspace';
import { VolumeDocumentEditor } from '../volume/VolumeDocumentEditor';
import { VolumeTOC } from '../volume/VolumeTOC';
import { WorkbookWorkspace } from '../volume/WorkbookWorkspace';
/**
 * ONLYOFFICE is the document editor: a volume with a document always opens it.
 * Legacy TipTap text is never hidden — it stays reachable until it has moved (SAD-1202).
 */ export function VolumeWorkspacePage() {
    const { id, volumeId, sectionId } = useParams();
    const volumes = useProjectVolumes(id);
    const documents = useVolumeDocuments(id, volumeId);
    const legacy = useVolumeLegacyText(id, volumeId);
    const writingOn = useFeatureFlag('writing_workspace_v1');
    const [search] = useSearchParams();
    if (!id || !volumeId) return null;
    const volume = (volumes.data ?? []).find((v)=>v.volumeId === volumeId);
    const loading = /*#__PURE__*/ _jsx("div", {
        className: "flex h-full items-center justify-center text-sm text-slate-500",
        children: "Loading volume..."
    });
    if (volumes.isLoading && !volume || documents.isLoading) return loading;
    if (!volumes.isLoading && !volume) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center text-sm text-rose-600",
            children: "Volume not found."
        });
    }
    if (!volume) return null;
    if (volumes.isError || documents.isError) {
        return /*#__PURE__*/ _jsx("div", {
            role: "alert",
            children: "Unable to load this volume. Reload to try again."
        });
    }
    const hasDocuments = (documents.data?.length ?? 0) > 0;
    const kind = volume.kind;
    // WRT-17: with writing_workspace_v1 on, a volume with a main document (or a
    // presentation) is written in the ONLYOFFICE workspace. Workbooks and
    // templates, and every volume with the flag off, route exactly as before.
    const mains = (documents.data ?? []).filter((d)=>!d.isSubmissionDoc);
    if (writingOn && kind !== 'workbook' && kind !== 'template' && (mains.length > 0 || kind === 'presentation')) {
        const main = mains.find((d)=>d.volumeDocumentId === search.get('doc')) ?? mains[0];
        return /*#__PURE__*/ _jsx(ProposalVolumeWorkspace, {
            documentId: main?.shirohigeDocumentId ?? '',
            projectId: id,
            sectionId: sectionId,
            volume: volume
        });
    }
    const isProse = kind !== 'workbook' && kind !== 'template' && kind !== 'presentation';
    // Only prose volumes and section links consult legacy text; skip the wait otherwise.
    const needsLegacy = !hasDocuments && (isProse || !!sectionId);
    // A failed legacy lookup must keep TipTap reachable, never the empty state.
    const showTipTap = needsLegacy && (legacy.isError || legacy.hasUnmovedText);
    if (needsLegacy && legacy.isLoading) return loading;
    let body;
    if (hasDocuments) {
        body = /*#__PURE__*/ _jsx(VolumeDocumentEditor, {
            projectId: id,
            volume: volume,
            initialSectionId: sectionId
        });
    } else if (showTipTap) {
        body = /*#__PURE__*/ _jsxs("div", {
            className: "flex h-full min-h-0 flex-col",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "shrink-0",
                    children: /*#__PURE__*/ _jsx(SectionMappingContext, {
                        projectId: id,
                        volumeId: volumeId,
                        sectionId: sectionId
                    })
                }),
                !legacy.isError && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    className: "shrink-0",
                    tone: "warning",
                    "data-testid": Testids.volume.legacyTextBanner,
                    children: [
                        "This text was written in the old section editor. It will move into the volume's Word document.",
                        legacy.orphanCount > 0 && ` ${legacy.orphanCount} older section texts are no longer attached to a section. An admin can recover them (WRT-20).`
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "min-h-0 flex-1",
                    children: /*#__PURE__*/ _jsx(SectionPadShell, {
                        projectId: id,
                        volume: volume,
                        sectionId: sectionId
                    })
                })
            ]
        });
    } else if (kind === 'workbook') {
        body = /*#__PURE__*/ _jsx(WorkbookWorkspace, {
            projectId: id,
            volume: volume
        });
    } else if (kind === 'template') {
        body = /*#__PURE__*/ _jsx(TemplateWorkspace, {
            projectId: id,
            volume: volume
        });
    } else {
        body = /*#__PURE__*/ _jsx(VolumeDocumentEditor, {
            projectId: id,
            volume: volume
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full min-h-0 flex-col md:flex-row",
        children: [
            /*#__PURE__*/ _jsx("aside", {
                className: "shrink-0 md:w-64",
                "aria-label": "Working outline",
                children: /*#__PURE__*/ _jsx(VolumeTOC, {
                    projectId: id,
                    volumeId: volumeId
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "min-h-0 min-w-0 flex-1 overflow-auto",
                children: body
            })
        ]
    });
}
